// CanvasTexture helpers (§12.5: vote cards, podium numerals, the pinball backglass and score reel,
// alphabet blocks). Without a DOM (unit tests) every texture is a 1×1 white DataTexture, so map
// views still build headless.
import { CanvasTexture, DataTexture, LinearFilter, NearestFilter, NoColorSpace, RGBAFormat, SRGBColorSpace, type Texture } from 'three';

export const DISPLAY_FONT = "'Lilita One', 'Arial Black', system-ui, sans-serif";
export const BODY_FONT = "'Nunito', system-ui, sans-serif";

export type Ctx2D = CanvasRenderingContext2D;

/** A canvas of the given size, or null without a DOM. */
export function makeCanvas(w: number, h: number): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function blankTexture(): DataTexture {
  const t = new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, RGBAFormat);
  t.needsUpdate = true;
  return t;
}

/** Runs `fn` again once the page's web fonts have loaded, when they have not yet (canvas text). */
export function afterFonts(fn: () => void): void {
  const fonts = typeof document !== 'undefined' ? document.fonts : undefined;
  if (!fonts || fonts.status === 'loaded') return;
  void fonts.ready.then(fn);
}

/**
 * Draws with `paint` into a w×h canvas and wraps it as an sRGB texture. Painted again once web
 * fonts are ready, so lettering never sticks in a fallback face.
 */
export function canvasTexture(w: number, h: number, paint: (g: Ctx2D, w: number, h: number) => void, pixelated = false, color = true): Texture {
  const c = makeCanvas(w, h);
  const g = c?.getContext('2d');
  if (!c || !g) return blankTexture();
  paint(g, w, h);
  const t = new CanvasTexture(c);
  afterFonts(() => {
    g.save();
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, w, h);
    g.restore();
    paint(g, w, h);
    t.needsUpdate = true;
  });
  if (!color) {
    t.colorSpace = NoColorSpace;
    t.magFilter = NearestFilter;
    t.minFilter = NearestFilter;
    t.generateMipmaps = false;
    return t;
  }
  t.colorSpace = SRGBColorSpace;
  t.anisotropy = 4;
  if (pixelated) {
    t.magFilter = NearestFilter;
    t.minFilter = LinearFilter;
    t.generateMipmaps = false;
  }
  return t;
}

/** A texture that can be repainted (score reel, vote cards). */
export interface LiveTexture {
  readonly texture: Texture;
  paint(fn: (g: Ctx2D, w: number, h: number) => void): void;
  dispose(): void;
}

export function liveTexture(w: number, h: number, pixelated = false): LiveTexture {
  const c = makeCanvas(w, h);
  const g = c?.getContext('2d') ?? null;
  const texture: Texture = c && g ? new CanvasTexture(c) : blankTexture();
  texture.colorSpace = SRGBColorSpace;
  if (pixelated) {
    texture.magFilter = NearestFilter;
    texture.minFilter = LinearFilter;
    texture.generateMipmaps = false;
  }
  return {
    texture,
    paint(fn) {
      if (!g) return;
      g.save();
      fn(g, w, h);
      g.restore();
      texture.needsUpdate = true;
    },
    dispose() {
      texture.dispose();
    },
  };
}

/** Rounded rectangle path. */
export function roundRect(g: Ctx2D, x: number, y: number, w: number, h: number, r: number): void {
  g.beginPath();
  g.moveTo(x + r, y);
  g.arcTo(x + w, y, x + w, y + h, r);
  g.arcTo(x + w, y + h, x, y + h, r);
  g.arcTo(x, y + h, x, y, r);
  g.arcTo(x, y, x + w, y, r);
  g.closePath();
}

/** Chunky sticker text: ink outline, white rim, then fill (§14.1 look). */
export function stickerText(g: Ctx2D, text: string, x: number, y: number, size: number, fill: string, ink = '#2A1A3A', rim = '#FFFFFF'): void {
  g.font = `${size}px ${DISPLAY_FONT}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.lineJoin = 'round';
  g.strokeStyle = ink;
  g.lineWidth = size * 0.28;
  g.strokeText(text, x, y + size * 0.06);
  g.strokeStyle = rim;
  g.lineWidth = size * 0.14;
  g.strokeText(text, x, y);
  g.fillStyle = fill;
  g.fillText(text, x, y);
}

/** A five-pointed star path centered at (x, y). */
export function starPath(g: Ctx2D, x: number, y: number, r: number, inner = 0.45): void {
  g.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 === 0 ? r : r * inner;
    if (i === 0) g.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    else g.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  g.closePath();
}
